import { z } from "zod";
import { AttachmentSchema } from "./attachment.js";
// ---------------------------------------------------------------------------
// Piste Jeux (G3a) — conversations de groupe : schémas partagés client/serveur.
// ---------------------------------------------------------------------------
/** Taille max d'un groupe (créateur inclus). */
export const GROUP_MAX_MEMBERS = 8;
// ── WebSocket ────────────────────────────────────────────────────────────────
export const JoinGroupEventSchema = z.object({
    type: z.literal("join_group"),
    groupId: z.string().uuid(),
});
export const GroupTurnArchivedEventSchema = z.object({
    type: z.literal("group_turn_archived"),
    groupId: z.string().min(1),
    turnId: z.string().min(1),
    authorId: z.string().min(1),
    authorName: z.string(),
    textFinal: z.string(),
    startedAt: z.number(),
    endedAt: z.number(),
    /** US-RM11 — photos du tour. Optionnel : un client N-1 l'ignore. */
    attachments: z.array(AttachmentSchema).optional(),
    /**
     * BE-23 — même forme que le 1:1 (`TurnArchivedEventSchema`), pour que le
     * client construise sa bulle depuis l'événement ou depuis le REST sans deux
     * types. À la gravure il est ABSENT PAR CONSTRUCTION : un tour ne peut pas
     * avoir été lu par les autres avant d'exister. Le renseigner ici serait un
     * mensonge — l'état lu arrive au prochain `GET /v1/groups/:id`.
     */
    readAt: z.number().nullable().optional(),
});
/** MAJ de la liste Discussions (nouveau tour, membre ajouté/parti, renommage). */
export const GroupActivityEventSchema = z.object({
    type: z.literal("group_activity"),
    groupId: z.string().min(1),
    lastActivityAt: z.string(),
    lastMessagePreview: z.string().nullable().optional(),
});
// ── REST ─────────────────────────────────────────────────────────────────────
/** Longueur d'un nom de groupe, partagée par la création et le renommage. */
export const GROUP_NAME_MAX = 40;
export const CreateGroupInputSchema = z.object({
    name: z.string().trim().min(1).max(GROUP_NAME_MAX),
    /** Contacts mutuels du créateur — cap GROUP_MAX_MEMBERS - 1. */
    memberIds: z.array(z.string().uuid()).min(1).max(GROUP_MAX_MEMBERS - 1),
});
/** Renommage (US-R81) — même contrainte de nom qu'à la création. */
export const UpdateGroupInputSchema = z.object({
    name: z.string().trim().min(1).max(GROUP_NAME_MAX),
});
/**
 * Ajout de membres (US-R81). Le cap du tableau est GROUP_MAX_MEMBERS et non
 * GROUP_MAX_MEMBERS - 1 : c'est la route qui sait combien de membres actifs le
 * groupe compte déjà, et le dépassement doit répondre GROUP_FULL — un plafond
 * de schéma le masquerait derrière un INVALID_INPUT qui n'explique rien.
 */
export const AddGroupMembersInputSchema = z.object({
    memberIds: z.array(z.string().uuid()).min(1).max(GROUP_MAX_MEMBERS),
});
/** Promotion / rétrogradation d'un membre (US-R82). */
export const SetGroupRoleInputSchema = z.object({
    role: z.enum(["admin", "member"]),
});
export const GroupParticipantDTOSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    role: z.enum(["admin", "member"]),
});
export const GroupDTOSchema = z.object({
    id: z.string(),
    name: z.string(),
    createdById: z.string(),
    createdAt: z.string(),
    participants: z.array(GroupParticipantDTOSchema),
    /** Liste Discussions : dernier message + non-lus. */
    lastMessagePreview: z.string().nullable(),
    lastActivityAt: z.string().nullable(),
    hasUnread: z.boolean(),
    /** Sourdine du membre courant : date ISO de fin, ou null si notifications actives. */
    mutedUntil: z.string().nullable(),
});
export const GroupTurnDTOSchema = z.object({
    turnId: z.string(),
    authorId: z.string(),
    authorName: z.string(),
    textFinal: z.string(),
    startedAt: z.number(),
    endedAt: z.number(),
    /** US-RM11 — photos du tour, URLs signées fraîches à chaque lecture. */
    attachments: z.array(AttachmentSchema).optional(),
    /**
     * BE-23 — état « lu » des tours OWN, dérivé (il n'existe aucune colonne
     * `readAt` sur `GroupTurn`) : instant où le DERNIER autre membre actif a lu,
     * soit le min des `lastReadAt` des autres membres, s'il dépasse `endedAt`.
     * `null` tant qu'un seul membre n'a pas lu ; ABSENT sur les tours des autres
     * (le rendu ne s'en sert que pour ses propres bulles). Optionnel et
     * additif : un client N-1 l'ignore, et le rendu est fail-soft — sans le
     * champ, la bulle own reste au contour « gravé ».
     */
    readAt: z.number().nullable().optional(),
});
// ── Sourdine ─────────────────────────────────────────────────────────────────
/**
 * Le client envoie une durée, pas une date : l'horloge d'un téléphone peut être
 * fausse ou trafiquée, et une date calculée côté client rendrait la sourdine
 * incohérente entre les appareils du même compte. Le serveur fait foi.
 */
export const MuteGroupInputSchema = z.object({
    duration: z.enum(["8h", "1w", "forever", "off"]),
});
/** « Toujours » = borne lointaine plutôt qu'un booléen : un seul champ nullable
 *  couvre les deux cas, et la requête de push reste un simple `mutedUntil > now`. */
export const MUTE_FOREVER_YEARS = 100;
export function muteDurationToDate(duration, now = new Date()) {
    switch (duration) {
        case "off":
            return null;
        case "8h":
            return new Date(now.getTime() + 8 * 60 * 60 * 1000);
        case "1w":
            return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        case "forever": {
            const forever = new Date(now);
            forever.setFullYear(forever.getFullYear() + MUTE_FOREVER_YEARS);
            return forever;
        }
    }
}
//# sourceMappingURL=groups.js.map